import { z } from 'zod';

import { mobilePhoneSchema } from '@/models/MobilePhone';
import { emptyMobilePhone } from '@/utils/phoneNumber';

import { UserStatus } from '../enums';
import type { UserTypeFilter } from '../enums/UserType';
import type { UserScopeType } from './UserScope';
import {
  inviteUserOperatorsBaseSchema,
  inviteUserCommercesSchema,
  inviteUserBranchesSchema,
  applyAccessScopeRefinements,
  defaultUserAccessScopeValues,
  hasValidPersonNameCharacters,
  personNameCharactersMessage,
} from './InviteUser';

export interface UserRoleApi {
  id: string;
  roleId: string;
  role: {
    id: string;
    name: string;
    description: string;
    /** `comercio` | `operador` | `external_pickup`. */
    restrictions?: string[];
  };
}

export interface ScopeIncludeExclude {
  include: string[];
  exclude: string[];
}

export interface BusinessUserScopes {
  logisticOperator: ScopeIncludeExclude;
  commerce: ScopeIncludeExclude;
  branch: ScopeIncludeExclude;
}

export interface ScopeResourceInfo {
  id: string;
  name: string;
  businessName?: string;
}

export interface BusinessUserResources {
  logisticOperator: Record<string, ScopeResourceInfo>;
  commerce: Record<string, ScopeResourceInfo>;
  branch: Record<string, ScopeResourceInfo>;
}

export interface BusinessUserMe {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  securityId: string | null;
  status: UserStatus | string;
  active: boolean;
  roles: string[];
  scopes: BusinessUserScopes;
  resources?: BusinessUserResources;
}

export interface UserAssignmentRef {
  id?: string;
  name?: string;
  scopeName?: string;
}

export interface UserListScope {
  scopeType: UserScopeType;
  scopeId: string | null;
  name?: string;
  scopeName?: string;
}

export interface UserApi {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  status?: UserStatus | string;
  isActive?: boolean;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  userRoles?: UserRoleApi[] | null;
  userType?: string;
  restriction?: string;
  includeAllOperators?: boolean;
  includeAllCommerces?: boolean;
  operators?: Array<UserAssignmentRef | string>;
  logisticOperators?: Array<UserAssignmentRef | string>;
  assignedOperators?: Array<UserAssignmentRef | string>;
  commerces?: Array<UserAssignmentRef | string>;
  assignedCommerces?: Array<UserAssignmentRef | string>;
  scopes?: UserListScope[];
}

export interface UserConfirmationOtp {
  id: string;
  used: boolean;
  attempts: number;
  sent: number;
  sent_at: string[];
  userId: string;
  email: string;
  rules: {
    type: string;
    source: string;
    generated_at: string;
  };
  otp: number;
  max_attempts: number;
  updatedAt: string;
  createdAt: string;
}

export interface CreateUserAndOtpResponse {
  user: UserApi;
  userConfirmationOtp: UserConfirmationOtp;
}

export interface UsersFilter {
  search?: string;
  phoneNumber?: string;
  userType?: UserTypeFilter | '';
  status?: UserStatus;
}

export const editUserPersonalSchema = z.object({
  firstName: z
    .string()
    .min(1, 'El nombre es obligatorio')
    .min(2, 'El nombre debe tener entre 2 y 255 caracteres')
    .max(255, 'El nombre debe tener entre 2 y 255 caracteres')
    .refine(hasValidPersonNameCharacters, personNameCharactersMessage('nombre')),
  lastName: z
    .string()
    .min(1, 'El apellido es obligatorio')
    .min(2, 'El apellido debe tener entre 2 y 255 caracteres')
    .max(255, 'El apellido debe tener entre 2 y 255 caracteres')
    .refine(hasValidPersonNameCharacters, personNameCharactersMessage('apellido')),
  email: z.string().min(1, 'El email es obligatorio').email('Email inválido'),
  phoneNumber: mobilePhoneSchema,
  active: z.boolean().optional(),
});

export type EditUserPersonalSchema = z.infer<typeof editUserPersonalSchema>;

export const defaultEditUserPersonalValues: EditUserPersonalSchema = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: emptyMobilePhone(),
  active: true,
};

const editUserAccessShape = z.object({
  operators: inviteUserOperatorsBaseSchema,
  commerces: inviteUserCommercesSchema,
  branches: inviteUserBranchesSchema,
});

export const buildEditUserWithAccessSchema = (userType?: string) =>
  editUserPersonalSchema
    .merge(editUserAccessShape)
    .superRefine((data, ctx) => applyAccessScopeRefinements(userType, data, ctx));

export type EditUserWithAccessSchema = z.infer<
  ReturnType<typeof buildEditUserWithAccessSchema>
>;

export const defaultEditUserWithAccessValues: EditUserWithAccessSchema = {
  ...defaultEditUserPersonalValues,
  ...defaultUserAccessScopeValues,
};
